import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, ArrowRight, Film } from "lucide-react";

import { toast } from "sonner";

const STATUS_COLORS = {
  delivered: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  in_production: "bg-blue-500/20 text-blue-400 border-blue-500/40",
  awaiting_assets: "bg-amber-500/20 text-amber-400 border-amber-500/40",
  pending_payment: "bg-slate-500/20 text-slate-400 border-slate-500/40",
};

export default function Dashboard() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => { api.get("/orders/mine").then(({ data }) => setOrders(data)); checkAuth(); }, []);

  const sub = user?.subscription;
  const active = sub?.status === "active";
  const otherPlan = sub?.plan_id === "starter" ? { id: "pro", name: "Pro" } : { id: "starter", name: "Starter" };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

  const switchPlan = async () => {
    try { await api.post("/subscriptions/change", { plan_id: otherPlan.id }); toast.success(`Switched to ${otherPlan.name} — effective next renewal`); checkAuth(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not switch plan"); }
  };
  const cancelPlan = async () => {
    if (!window.confirm("Cancel your plan? You'll keep any remaining credits but won't be charged again.")) return;
    try { await api.post("/subscriptions/cancel"); toast.success("Subscription canceled"); checkAuth(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not cancel"); }
  };

  return (
    <div className="bg-[#090A0F] min-h-screen"><Header />
      <div className="max-w-6xl mx-auto px-6 pt-32 pb-16 fade-up">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-2">Your Studio</p>
            <h1 className="font-display text-4xl text-white">Welcome, {user?.name?.split(" ")[0]}</h1>
          </div>
          <Button onClick={() => navigate("/order/most-popular")} className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full" data-testid="new-order-button">
            <Plus className="w-4 h-4 mr-1.5" /> New Film
          </Button>
        </div>

        <div className="glass rounded-2xl p-6 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="subscription-panel">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Membership</p>
            <p className="font-display text-2xl text-white">
              {active ? `${sub.plan_name} Plan` : sub?.status === "canceled" ? `${sub.plan_name} Plan (canceling)` : "No active plan"}
            </p>
            <p className="text-sm text-[#D4AF37]" data-testid="credit-balance">
              {(user?.credits || 0)} film credit{(user?.credits || 0) === 1 ? "" : "s"} available
            </p>
            {active && sub?.next_topup_at && (
              <p className="text-xs text-slate-400 mt-1" data-testid="renewal-date">Renews {fmtDate(sub.next_topup_at)} · ${sub.price}/mo{sub.renewals > 0 ? ` · ${sub.renewals} renewal${sub.renewals === 1 ? "" : "s"}` : ""}</p>
            )}
          </div>
          <div className="flex gap-2">
            {active ? (
              <>
                <Button onClick={switchPlan} variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] rounded-full hover:bg-[#D4AF37]/10" data-testid="switch-plan-button">Switch to {otherPlan.name}</Button>
                <Button onClick={cancelPlan} variant="ghost" className="text-slate-400 hover:text-red-400 rounded-full" data-testid="cancel-plan-button">Cancel</Button>
              </>
            ) : (
              <Button onClick={() => navigate("/pricing")} variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] rounded-full hover:bg-[#D4AF37]/10" data-testid="manage-plan-button">
                {sub?.status === "canceled" ? "Resubscribe" : "Get a Plan"}
              </Button>
            )}
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="glass rounded-2xl p-16 text-center" data-testid="empty-orders">
            <Film className="w-12 h-12 text-[#D4AF37]/60 mx-auto mb-4" />
            <h3 className="font-display text-2xl text-white mb-2">No films yet</h3>
            <p className="text-slate-400 mb-6">Create your first cinematic property film.</p>
            <Button onClick={() => navigate("/")} className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full">Browse products</Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => (
              <button key={o.id} onClick={() => navigate(o.status === "awaiting_assets" ? `/orders/${o.id}/submit` : `/orders/${o.id}`)}
                className="w-full text-left glass rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4 hover:border-[#D4AF37]/40 transition-all group"
                data-testid={`order-row-${o.order_number}`}>
                <div>
                  <p className="font-mono text-xs text-slate-500">{o.order_number}</p>
                  <p className="font-display text-xl text-white">{o.product_name}</p>
                  <p className="text-sm text-slate-400">{o.property?.name || "—"}</p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="font-mono text-[#D4AF37]">${o.collected_revenue?.toLocaleString()}</p>
                    {o.is_complimentary && <p className="text-xs text-slate-500">Comp</p>}
                  </div>
                  <Badge className={STATUS_COLORS[o.status] || STATUS_COLORS.pending_payment}>{o.status.replace(/_/g, " ")}</Badge>
                  <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
